/**
 * Archivo: VentanaEnviarMensaje.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define una ventana modal para seleccionar y enviar mensajes predefinidos a un colegial específico.
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^3.0.1
 * - Servicios/MensajesServicios: Funciones para interactuar con el servicio de mensajes.
 * 
 * Propósito:
 * Permitir al usuario seleccionar uno o varios mensajes fijos y enviarlos a un colegial.
 */

import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import '../Estilos/estilos.css';
import cruzNegra from '../Imagenes/Iconos/CruzNegra.png';
import { listaMensajesFijos, añadirMensajeUsuario } from '../Servicios/MensajesServicios';

const VentanaEnviarMensaje = ({ onClose, idColegial }) => {
    const [mensajesFijos, setMensajesFijos] = useState([]);
    const [mensajesSeleccionados, setMensajesSeleccionados] = useState([]);
    const [mensajeError, setMensajeError] = useState('');
    const [mensajeExito, setMensajeExito] = useState('');
    const [mostrarCampoPersonalizado, setMostrarCampoPersonalizado] = useState(false);
    const [mensajePersonalizado, setMensajePersonalizado] = useState('');


    useEffect(() => {
        const obtenerMensajesFijos = async () => {
            try {
                const token = Cookies.get('token');
                const result = await listaMensajesFijos(token);

                if (result.success) {
                    setMensajesFijos(result.data);
                } else {
                    setMensajeError(result.message);
                }
            } catch (error) {
                setMensajeError('Error al obtener los mensajes fijos');
            }
        };

        obtenerMensajesFijos();
    }, []);

    const manejarSeleccionMensaje = (mensajeId) => {
        setMensajesSeleccionados((prev) =>
            prev.includes(mensajeId)
                ? prev.filter((id) => id !== mensajeId)
                : [...prev, mensajeId]
        );
    };

    const manejarEnviarMensajes = async () => {
        if (!mostrarCampoPersonalizado && mensajesSeleccionados.length === 0) {
            setMensajeError('Por favor, selecciona al menos un mensaje o escribe un mensaje personalizado.');
            return;
        }
    
        try {
            const token = Cookies.get('token');
            if (mostrarCampoPersonalizado) {
                const result = await añadirMensajeUsuario(token, null, idColegial, mensajePersonalizado);
                if (!result.success) {
                    setMensajeError(result.message);
                    return;
                }
            } else {
                for (const idMensaje of mensajesSeleccionados) {
                    const result = await añadirMensajeUsuario(token, idMensaje, idColegial);
                    if (!result.success) {
                        setMensajeError(result.message);
                        return;
                    }
                }
            }
            setMensajeError('');
            setMensajeExito('Mensaje enviado con éxito');
            setMensajesSeleccionados([]);
            setMensajePersonalizado('');
            setMostrarCampoPersonalizado(false);
        } catch (error) {
            setMensajeExito('');
            setMensajeError('Error al enviar los mensajes');
        }
    };
    

    return (
        <div className="container col-xl-12 col-lg-12 col-md-12 bdr-1 rounded p-xl-2 p-lg-1 p-md-0 position-fixed" style={{ fontWeight: '3rem', background: '#D9D9D9', top: '44%', left: '50%', transform: 'translate(-50%, -40%)', zIndex: '5' }}>
            <div className="row-fluid pb-0 d-flex">
                <div className="container col-12" style={{ borderBottom: '2px solid white' }}>
                    <div className='row-fluid p-0 d-flex'>
                        <div className='col-xl-11 col-lg-11 col-md-10'>
                            <span className="customFontSizeTextoGrande text-black"> Seleccione el mensaje que quieres enviar </span>
                        </div>
                        <div className='col-1'>
                            <img
                                src={cruzNegra}
                                alt="close-icon"
                                className="card-img-top"
                                style={{ position: 'absolute', height: '3rem', width: '3rem', cursor: 'pointer', top: 0, right: 0 }}
                                onClick={onClose}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className='container-fluid pb-3 pt-3'>
                <form onSubmit={(e) => { e.preventDefault(); manejarEnviarMensajes(); }}>
                    <div className="form-group">
                        {mensajesFijos.map((mensaje) => (
                            <div key={mensaje.id_mensaje} className="form-check p-2 d-flex">
                                <div>
                                    <label className="form-check-label" htmlFor={`mensaje_${mensaje.id_mensaje}`}>
                                        {mensaje.texto_mensaje}
                                    </label>
                                </div>
                                <div className='ml-4'>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        value={mensaje.id_mensaje}
                                        onChange={() => manejarSeleccionMensaje(mensaje.id_mensaje)}
                                        id={`mensaje_${mensaje.id_mensaje}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                    {/**Opción para enviar mensajes personalizados */}
                    <div className="form-check p-2 d-flex">
                        <div>
                            <label className="form-check-label">
                                Mensaje personalizado:
                            </label>
                        </div>
                        <div>
                            <input
                                className="form-check-input ml-2"
                                type="checkbox"
                                onChange={() => setMostrarCampoPersonalizado(!mostrarCampoPersonalizado)}
                                checked={mostrarCampoPersonalizado}
                            />
                        </div>
                    </div>
                    {mostrarCampoPersonalizado && (
                        <div className="form-group">
                            <textarea
                                className="form-control"
                                value={mensajePersonalizado}
                                onChange={(e) => setMensajePersonalizado(e.target.value)}
                                placeholder="Escribe el mensaje aquí"
                                rows="3"
                            />
                        </div>
                    )}
                    <div className="d-flex justify-content-center mt-3">
                        <button type="submit" className='btn text-white' style={{ backgroundColor: 'Black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                            Enviar mensaje
                        </button>
                    </div>
                </form>
                {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
            </div>
        </div>
    );
};

export default VentanaEnviarMensaje;
